import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { i18n } from '@/app/i18n'
import { routes } from '@/router/routes'
import { setSessionExpiredHandler } from '@/shared/api/auth-tokens'
import { useAuthStore } from '@/stores/auth.store'
import { useBranchScopeStore } from '@/stores/branch-scope.store'

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0, behavior: 'smooth' }
  },
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  const authStore = useAuthStore()

  // Restore the session from stored tokens on the first navigation (idempotent after boot).
  await authStore.ensureSessionLoaded()

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return {
      name: 'login',
      query: to.fullPath === '/' ? undefined : { redirect: to.fullPath },
    }
  }

  // A temporary password blocks every screen except the forced change-password page (auth.md §8).
  if (authStore.isAuthenticated && authStore.mustChangePassword && to.name !== 'change-password') {
    return { name: 'change-password' }
  }

  if (authStore.isAuthenticated && !authStore.mustChangePassword && to.name === 'change-password') {
    return { name: authStore.firstAllowedRouteName }
  }

  if (to.meta.guestOnly && authStore.isAuthenticated) {
    return { name: authStore.firstAllowedRouteName }
  }

  if (authStore.isAuthenticated && to.meta.requiresAuth) {
    const routeName = typeof to.name === 'string' ? to.name : ''

    if (routeName && routeName !== 'change-password' && !authStore.canAccessRoute(routeName)) {
      return { name: authStore.firstAllowedRouteName }
    }

    // Resolve the branch scope before any branch-scoped view can mount — every scoped endpoint
    // requires branchId and answers 400 without it. Single-flight, idempotent after boot.
    if (!authStore.mustChangePassword) {
      await useBranchScopeStore().bootstrap()
    }
  }

  return true
})

// When a background request fails to refresh, drop the session and return to login (guarded
// against a redirect-to-self loop).
setSessionExpiredHandler(() => {
  const authStore = useAuthStore()
  // Dropping the session also drops the branch scope — branch-scope.store watches it.
  authStore.clearSession()

  if (router.currentRoute.value.name !== 'login') {
    void router.replace({ name: 'login' })
  }
})

router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  // A query-only move (a modal's ?form=pay) stays on the same page, whose title AppShell has already
  // filled in — "Buyurtma #12", which the bare key would reset to "Buyurtma #".
  if (from.matched.length && to.path === from.path) return
  const titleKey = typeof to.meta.titleKey === 'string' ? to.meta.titleKey : 'app.name'
  document.title = `${i18n.global.t(titleKey)} · ${i18n.global.t('app.name')}`
})
